import {
    BackendType,
    FrontendType,
    ObjectType,
    StarjackDate,
} from '@/models/general';
import { Money } from '@/models/money';
import { convertDateFromDateArray } from '@/utils/time';

export type Coupon<T extends ObjectType = FrontendType> = {
    active: boolean;
    id: string;
    name: string;
    amount: Money;
    validFrom?: StarjackDate<T>;
    validUntil: StarjackDate<T>;
    createdAt: string;
    liftOperatorId?: string;
    liftOperatorName?: string;
    codes: Code[];
};

export type CreateCoupon<T extends ObjectType = FrontendType> = {
    name: string;
    active: boolean;
    contingent: string;
    amount: Money;
    validFrom?: T extends FrontendType ? Date : string;
    validUntil: T extends FrontendType ? Date : string;
    codes: string;
    liftOperatorId?: string;
};

export type Code = {
    code: string;
    contingent: number;
    id: string;
    used: number;
    usages?: CodeUsage[];
};

export type CodeUsage = {
    id: string;
    customerId: string;
    customerEmail?: string;
    usedAt: string;
};

export const createNewCoupon = (): CreateCoupon => ({
    name: '',
    active: true,
    codes: '',
    contingent: '',
    amount: {
        amount: 0,
        currency: 'EUR',
    },
    validFrom: null,
    validUntil: null,
    liftOperatorId: undefined,
});

// One row of the paged overview: aggregated counters only — redemption details
// live on the detail endpoint.
export type CouponListItem<T extends ObjectType = FrontendType> = {
    id: string;
    name: string;
    amount: Money;
    validFrom?: StarjackDate<T>;
    validUntil: StarjackDate<T>;
    createdAt: string;
    active: boolean;
    liftOperatorId?: string;
    liftOperatorName?: string;
    codeCount: number;
    usedCount: number;
};

export type CouponPage = {
    coupons: CouponListItem[];
    total: number;
    page: number;
    size: number;
};

export const convertCouponListItemFromResponse = (
    item: CouponListItem<BackendType>
): CouponListItem => ({
    ...item,
    validFrom: convertDateFromDateArray(item.validFrom),
    validUntil: convertDateFromDateArray(item.validUntil),
});

export const convertCouponFromResponse = (
    coupon: Coupon<BackendType>
): Coupon => ({
    ...coupon,
    validFrom: convertDateFromDateArray(coupon.validFrom),
    validUntil: convertDateFromDateArray(coupon.validUntil),
});

export const getNUmberFromContingent = (contingent: string): number | null => {
    const number = parseInt(contingent, 10);
    return isNaN(number) ? null : number;
};

export const isOfTypeCoupon = (
    toBeDetermined: CreateCoupon | Coupon
): toBeDetermined is Coupon => {
    return !!(toBeDetermined as Coupon)?.id;
};

export const isCreateCouponComplete = (contingent: CreateCoupon): boolean => {
    return (
        contingent.name !== '' &&
        contingent.codes !== '' &&
        contingent.contingent !== '' &&
        contingent.amount.amount > 0 &&
        contingent.validUntil !== null
    );
};
